import "./AccountPerformanceChartComponent.scss";
import {IComponentOptions, IFilterService, IScope} from "angular";
import {ChartData} from "../../../strategybuilder/components/stock-chart/models/StockChartData";
import {AccountPerformanceViewStore} from "../../service/AccountPerformanceViewStore";
import {Pair, PairOf} from "../../../types/GenericTypes";
import {NVD3ChartController} from "../../../components/charts/common/NVD3ChartController";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {PerformanceChartData} from "../../models/PerformanceChart";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {IsoDate} from "../../../pseudo-types/DateTypes";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {Optional} from "../../../js/util/Optional";
import {TimeFrameRange} from "../../models/TimeFrame";
import moment = require("moment");
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {Branding} from "../../../types/Branding";
import {AsxGameService} from "../../../game/service/AsxGameService";

export const AccountPerformanceChart = "tfAccountPerformanceChart";

export function accountPerformanceChart(parentDir: string): IComponentOptions {
    return {
        templateUrl: `${parentDir}/account-performance-chart/AccountPerformanceChartComponent.html`,
        bindings: {
            viewStore: "<"
        },
        controllerAs: "ctrl",
        controller: AccountPerformanceChartComponentController
    };
}

export class AccountPerformanceChartComponentController extends NVD3ChartController {

    public readonly viewStore: AccountPerformanceViewStore;

    public chartData: ChartData<Pair<number>> = [];
    public xLabels: Array<string> = [];
    public chartOptions: any;

    private readonly translateFilter: (value: string) => string;
    private readonly chartSelectionIndexRange = new ReplaySubject<Pair<number>>(1);

    /** @ngInject */
    public constructor(systemEventService: SystemEventService,
                       $filter: IFilterService,
                       public $scope: IScope,
                       private readonly currentUserService: CurrentUserService,
                       private asxGameService: AsxGameService) {
        super(systemEventService);
        this.translateFilter = $filter("translate");
    }

    public $onInit() {
        super.$onInit();
        this.watchChartData();
        this.initFocusRange();
        this.watchDataRange();
        this.initChartOptions();
    }

    private watchChartData() {
        Observable.combineLatest(this.viewStore.chartData,
                                 this.currentUserService.current)
                  .takeUntil(this.destroyed$)
                  .subscribe(([d, user]) => {
                      if (user.isBranding(Branding.GAME)) {
                          this.asxGameService.dates().then(dates => {
                              const filteredData = d.filter(chartData => new Date(chartData.asAt) >= new Date(dates.opens));
                              this.updateChartData(filteredData);
                          });
                      } else {
                          this.updateChartData(d);
                      }
                  });
    }

    /* Update the chart range from the values passed to us by the chart's focus area */
    private initFocusRange() {
        const selection$ = this.chartSelectionIndexRange.skip(1).debounceTime(100);
        Observable.combineLatest(this.viewStore.chartData, selection$)
                  .observeOn(Scheduler.async)
                  .takeUntil(this.destroyed$)
                  .map(([data, range]) => this.dataDateRange(data, range))
                  .subscribe((range) => this.viewStore.updateDataRange(range));
    }

    private dataDateRange(data: PerformanceChartData, indexRange: Pair<number>): Pair<IsoDate> {
        const start = Optional.of(indexRange)
                              .map((range) => range[0])
                              .map(Math.floor)
                              .map((d) => Math.max(d, 0))
                              .orElse(0);

        const maxIndex = data.length - 1;

        const end = Optional.of(indexRange)
                            .map((range) => range[1])
                            .map(Math.ceil)
                            .map((i) => Math.min(i, maxIndex))
                            .orElse(maxIndex);

        const dataStart = data[start];
        const dataEnd = data[end];

        const isoStart = DateTimeUtil.momentToIsoDate(moment(dataStart.asAt));
        const isoEnd = DateTimeUtil.momentToIsoDate(moment(dataEnd.asAt));

        return PairOf(isoStart, isoEnd);
    }

    private watchDataRange() {
        Observable.combineLatest(this.viewStore.chartData, this.viewStore.timeFrameRange)
                  .observeOn(Scheduler.async)
                  .takeUntil(this.destroyed$)
                  .filter(([_, range]) => range != null)
                  .map(([data, range]) => this.indexForDates(data, range!))
                  .subscribe((range) => this.updateFocusRange(range));
    }

    private indexForDates(data: PerformanceChartData, timeFrame: TimeFrameRange): Pair<number> {
        const dateRange = timeFrame.range;
        const dates = data.map((d) => d.asAt)
                          .map((d) => DateTimeUtil.momentToIsoDate(moment(d)));
        const start = dateRange[0];
        const end = dateRange[1];

        const beforeStart = dates.filter((d) => d.localeCompare(start) < 0);
        const indexStart = beforeStart.length;

        const afterEnd = dates.filter((d) => d.localeCompare(end) > 0);
        const dataSize = data.length;
        const maxIndex = dataSize - 1;
        const indexEnd = maxIndex - afterEnd.length;

        return PairOf(indexStart, indexEnd);
    }

    private updateFocusRange(range: Pair<number>) {
        const newChartOptions = {
            ...this.chartOptions,
            chart: {
                ...this.chartOptions.chart,
                brushExtent: range
            }
        };
        this.$scope.$applyAsync(() => this.chartOptions = newChartOptions);
    }

    private updateFromBrushExtents(extent: Pair<number>) {
        this.chartSelectionIndexRange.next(extent);
        // Horrible hack to get around the other horrible hacks that are in here...
        this.chartOptions.chart.brushExtent = extent;
    }

    private initChartOptions() {
        this.chartOptions = {
            chart: {
                type: "lineWithFocusChart",
                height: 520,
                margin: {
                    top: 20,
                    right: 70,
                    bottom: 50,
                    left: 70
                },
                brushExtent: null,
                focus: {
                    dispatch: {
                        onBrush: (extent: Pair<number>) => this.updateFromBrushExtents(extent)
                    }
                },
                interpolate: "linear",
                showXAxis: true,
                x: (d: [number, number]) => {
                    if (d == null) {
                        return null;
                    } else {
                        return d[0];
                    }
                },
                y: (d: [number, number]) => {
                    if (d == null) {
                        return null;
                    } else {
                        return d[1];
                    }
                },
                xAxis: {
                    axisLabel: this.translateFilter("common.AS_AT"),
                    tickFormat: (i: number) => {
                        return this.xLabels[i];
                    },
                    showMaxMin: true
                },
                x2Axis: {
                    axisLabel: this.translateFilter("common.AS_AT"),
                    tickFormat: (i: number) => {
                        return this.xLabels[i];
                    },
                    showMaxMin: true
                },
                yAxis: {
                    axisLabel: "NLV",
                    showMaxMin: true
                },
                color: d3.scale.category10().range(),
                duration: 0,
                useInteractiveGuideline: true,
                clipVoronoi: false
            }
        };
    }

    private updateChartData(data: PerformanceChartData) {
        const format = d3.time.format("%d/%m/%y %H:%M");

        const nlvData = data.map((d, i) => PairOf(i, d.nlv));

        this.xLabels = data.map(d => new Date(d.asAt))
                           .map(d => format(d));

        this.chartData = [
            {
                key: "NLV",
                values: nlvData
            }
        ];
    }
}
